import React from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Button, 
  Container,
  Grid,
  useTheme
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import NatureIcon from '@mui/icons-material/Nature';
import BarChartIcon from '@mui/icons-material/BarChart';
import TimelineIcon from '@mui/icons-material/Timeline';
import CloudIcon from '@mui/icons-material/Cloud';

const HomePage = () => {
  const theme = useTheme();
  
  return (
    <Box sx={{ 
      flexGrow: 1, 
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      background: 'linear-gradient(rgba(10, 25, 41, 0.8), rgba(10, 25, 41, 0.9)), url("/images/earth-bg.jpg")',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    }}>
      {/* 히어로 섹션 */}
      <Box 
        sx={{ 
          py: { xs: 8, md: 0 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center'
        }}
      >
        <Container maxWidth="md">
          <Typography 
            variant="h2" 
            component="h1" 
            sx={{ 
              fontWeight: 'bold',
              mb: 3,
              fontSize: { xs: '2.5rem', md: '3.5rem' }
            }}
          >
            탄소 발자국 관리 시스템
          </Typography>
          
          <Typography 
            variant="h5" 
            sx={{ 
              mb: 4,
              color: 'text.secondary',
              maxWidth: '800px',
              mx: 'auto'
            }}
          >
            기업과 개인의 탄소 배출량을 추적, 분석하고 감축 목표를 달성하는 데 도움을 주는 종합 솔루션
          </Typography>
          
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Button 
              component={RouterLink} 
              to="/dashboard" 
              variant="contained" 
              size="large"
              endIcon={<ArrowForwardIcon />}
              sx={{ px: 4, py: 1.5 }}
            >
              시작하기
            </Button>
            
            <Button 
              component={RouterLink} 
              to="/register" 
              variant="outlined" 
              size="large"
              sx={{ px: 4, py: 1.5 }}
            >
              회원가입
            </Button>
          </Box>
        </Container>
      </Box>
      
      {/* 특징 섹션 */}
      <Box sx={{ py: 8, backgroundColor: 'background.default' }}>
        <Container maxWidth="lg">
          <Typography 
            variant="h3" 
            component="h2" 
            sx={{ 
              textAlign: 'center', 
              mb: 6,
              fontWeight: 'bold'
            }}
          >
            주요 기능
          </Typography>
          
          <Grid container spacing={4}>
            <Grid item xs={12} md={6} lg={3}>
              <Card sx={{ height: '100%', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-8px)' } }}>
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    mb: 2,
                    backgroundColor: 'primary.main',
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    mx: 'auto',
                    alignItems: 'center'
                  }}>
                    <NatureIcon sx={{ fontSize: 36, color: 'white' }} />
                  </Box>
                  <Typography variant="h5" component="h3" sx={{ mb: 2, fontWeight: 'medium' }}>
                    활동 데이터 추적
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    교통, 전기, 난방 등 다양한 활동의 탄소 배출량을 간편하게 기록하고 추적합니다.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6} lg={3}>
              <Card sx={{ height: '100%', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-8px)' } }}>
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    mb: 2,
                    backgroundColor: theme.palette.chart.c2,
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    mx: 'auto',
                    alignItems: 'center'
                  }}>
                    <BarChartIcon sx={{ fontSize: 36, color: 'white' }} />
                  </Box>
                  <Typography variant="h5" component="h3" sx={{ mb: 2, fontWeight: 'medium' }}>
                    데이터 시각화
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    직관적인 차트와 그래프로 탄소 배출 패턴을 쉽게 이해하고 분석합니다.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6} lg={3}>
              <Card sx={{ height: '100%', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-8px)' } }}>
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    mb: 2,
                    backgroundColor: theme.palette.chart.c3,
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    mx: 'auto',
                    alignItems: 'center'
                  }}>
                    <TimelineIcon sx={{ fontSize: 36, color: 'white' }} />
                  </Box>
                  <Typography variant="h5" component="h3" sx={{ mb: 2, fontWeight: 'medium' }}>
                    예측 분석
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    고급 알고리즘을 통해 미래 탄소 배출량을 예측하고 감축 목표를 설정합니다.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6} lg={3}>
              <Card sx={{ height: '100%', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-8px)' } }}>
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    mb: 2,
                    backgroundColor: theme.palette.chart.c4,
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    mx: 'auto',
                    alignItems: 'center'
                  }}>
                    <CloudIcon sx={{ fontSize: 36, color: 'white' }} />
                  </Box>
                  <Typography variant="h5" component="h3" sx={{ mb: 2, fontWeight: 'medium' }}>
                    클라우드 기반
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    언제 어디서나 접근 가능한 클라우드 기반 솔루션으로 데이터를 안전하게 관리합니다.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>
      
      {/* CTA 섹션 */}
      <Box 
        sx={{ 
          py: 8, 
          backgroundColor: 'background.paper',
          textAlign: 'center'
        }}
      >
        <Container maxWidth="md">
          <Typography 
            variant="h3" 
            component="h2" 
            sx={{ 
              mb: 3,
              fontWeight: 'bold'
            }}
          >
            지금 시작하세요
          </Typography>
          
          <Typography 
            variant="h6" 
            sx={{ 
              mb: 4,
              color: 'text.secondary',
              maxWidth: '700px',
              mx: 'auto'
            }}
          >
            탄소 발자국을 줄이고 지속 가능한 미래를 위한 첫 걸음을 내딛으세요.
          </Typography>
          
          <Button 
            component={RouterLink} 
            to="/register" 
            variant="contained" 
            size="large"
            endIcon={<ArrowForwardIcon />}
            sx={{ px: 5, py: 1.5 }}
          >
            무료로 시작하기
          </Button>
        </Container>
      </Box>
      
      {/* 푸터 */}
      <Box 
        sx={{ 
          py: 4, 
          backgroundColor: 'background.default',
          borderTop: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={2} justifyContent="space-between" alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                &copy; {new Date().getFullYear()} (주)흠소프트. 모든 권리 보유.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
              <Box sx={{ display: 'flex', gap: 3, justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                <Typography variant="body2" component="a" href="#" color="text.secondary" sx={{ textDecoration: 'none' }}>
                  개인정보처리방침
                </Typography>
                <Typography variant="body2" component="a" href="#" color="text.secondary" sx={{ textDecoration: 'none' }}>
                  이용약관
                </Typography>
                <Typography variant="body2" component="a" href="#" color="text.secondary" sx={{ textDecoration: 'none' }}>
                  문의하기
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default HomePage;
